'use client';

import { Tabs } from '@/components/Tabs';
import useCreateQueryString from '@/hooks/useCreateQueryString';
import useRouterTransition from '@/utils/useRouterTransition';
import { useParams, useSearchParams } from 'next/navigation';
import { createContext } from 'react';

export { TabsContent } from '@/components/ui/tabs';

const NavigationTabsContext = createContext({});

export default function NavigationTabs({
  children,
  tabs = [],
  value,
  queryKey, // required for internal logic if using default handlers
  staticParams, // optional
  basePath, // required if using default routing
  onChange: customOnChange,
  onHover: customOnHover
}) {
  const router = useRouterTransition();
  const searchParams = useSearchParams();
  const { locale } = useParams();
  const createQueryString = useCreateQueryString(searchParams);

  const formatParams = val => {
    if (!queryKey) return [];
    const dynamic = [queryKey, val];
    const staticEntries = staticParams ? Object.entries(staticParams).filter(([_, value]) => !!value) : [];
    return [dynamic, ...staticEntries];
  };

  const defaultOnChange = val => {
    if (!basePath || !queryKey) return; // safely do nothing if basePath or queryKey not provided
    const query = createQueryString(...formatParams(val));
    router.push(`/${locale}${basePath}?${query}`);
  };

  const defaultOnHover = val => {
    if (!basePath || !queryKey) return;
    const query = createQueryString(...formatParams(val));
    router.prefetch(`/${locale}${basePath}?${query}`);
  };

  return (
    <NavigationTabsContext.Provider value={{ isPending: router?.isPending }}>
      <Tabs
        list={tabs}
        value={value}
        onChange={customOnChange || defaultOnChange}
        onHover={customOnHover || defaultOnHover}
      >
        {children}
      </Tabs>
    </NavigationTabsContext.Provider>
  );
}
